import type { TimeUnit } from "chart.js";
import dayjs from "dayjs";

import { INTERVALS } from "./chart-types";

export type ChartDataPoint = {
  x: number;
  y: number;
};

export function groupByIntervalAndAverage(data: ChartDataPoint[], minDate: Date, maxDate: Date): ChartDataPoint[] {
  const groupedData: Record<string, { sum: number; count: number }> = {};
  const interval = determineXTimeInput(minDate, maxDate);

  data.forEach(point => {
    const date = new Date(point.x || "");
    let intervalKey: number;
    if (interval === "quarter") {
      intervalKey = dayjs(date).startOf("year").toDate().getTime();
    } else if (interval && interval !== "hour") {
      intervalKey = dayjs(date)
        .startOf(interval as dayjs.ManipulateType)
        .toDate()
        .getTime();
    } else {
      intervalKey = date.getTime();
    }

    if (!(intervalKey in groupedData)) {
      groupedData[intervalKey] = { sum: 0, count: 0 };
    }

    groupedData[intervalKey].sum += point.y || 0;
    groupedData[intervalKey].count += 1;
  });

  return Object.entries(groupedData).map(([key, value]) => ({
    x: parseInt(key),
    y: value.sum / value.count,
  }));
}

export const determineXTimeInput = (minDate: Date, maxDate: Date): TimeUnit | undefined => {
  if (dayjs(maxDate).diff(minDate, "day") < 7) {
    return "hour";
  } else if (dayjs(maxDate).diff(minDate, "week") < 4) {
    return "day";
  } else if (dayjs(maxDate).diff(minDate, "month") < 12) {
    return "week";
  } else if (dayjs(maxDate).diff(minDate, "year") <= 5) {
    return "month";
  } else if (dayjs(maxDate).diff(minDate, "year") <= 15) {
    return "year";
  }
  return "quarter" as TimeUnit;
};

export function getUnit(range: number) {
  switch (range) {
    case INTERVALS.Year:
      return "month";
    case INTERVALS.Month:
      return "week";
    case INTERVALS.Week:
      return "day";
    case INTERVALS.Day:
    default:
      return "hour";
  }
}

function numDigits(x: number) {
  return 10 ** (Math.floor(x / 10) * 10).toString().length;
}

export function getMin(minY: number, paddingY: number) {
  let min = 0;
  const digits = numDigits(paddingY);
  if (paddingY < 1) {
    min = Number((minY - paddingY).toFixed(1));
  } else {
    min = Math.floor((minY - paddingY) / digits) * digits;
  }
  return Math.max(0, min < 1 ? min : Math.floor(min));
}

export function getMax(maxY: number, paddingY: number) {
  let max = 0;
  const digits = numDigits(paddingY);
  if (paddingY < 1) {
    max = Number((maxY + paddingY).toFixed(1));
  } else {
    max = Math.ceil((maxY + paddingY) / digits) * digits;
  }
  return Math.max(0, max < 1 ? max : Math.ceil(max));
}
